/**
 * Ports `report_agent_crash` (entrypoint.sh:936-943): the pod-side crash
 * supervisor's best-effort POST to the API so the crash gets posted to the
 * task's activity log (which also keeps the pod alive through recovery) and,
 * on the final attempt, a durable notification. Payload contract matches
 * `bootstrap-route.ts`'s `parseCrashReport`: `{ exitCode, attempt, final }`.
 *
 * Best-effort, mirrors the bash `|| true`: a failed or slow report must never
 * block the supervisor's restart loop, so every path swallows errors and this
 * never throws.
 */
const CRASH_REPORT_TIMEOUT_MS = 10_000;

export interface CrashReport {
  exitCode: number;
  attempt: number;
  final: boolean;
}

export async function reportAgentCrash(
  apiUrl: string,
  bootstrapToken: string,
  report: CrashReport,
  fetchFn: typeof fetch = fetch,
): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CRASH_REPORT_TIMEOUT_MS);
  try {
    await fetchFn(`${apiUrl}/api/v3/pods/agent-crash`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${bootstrapToken}`,
      },
      body: JSON.stringify(report),
      signal: controller.signal,
    });
  } catch {
    // Best-effort — never let a network failure or timeout block the retry loop.
  } finally {
    clearTimeout(timer);
  }
}
